import { useMemo, useState } from 'react';

import {
  ENZYME_CHALLENGES,
  ENZYME_EXPLORE_FIELDS,
  ENZYME_QUESTION,
  calculateEnzymeExplore,
  type EnzymeInputKey,
} from './enzymeActivityExplore';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import {
  ENZYMES,
  PEAK_ACTIVITY,
  PH_RANGE,
  TEMPERATURE_RANGE,
  activityCurve,
} from './polymersBiomoleculesFood';
import { ModelLineChart } from './PolymersFoodChart';
import { CARD_CLASS, chipClass } from './polymersStyles';

const DEFAULT_INPUT: Record<EnzymeInputKey, number> = { temperatureC: 25, pH: 7 };

function formatPct(value: number): string {
  return `${value.toLocaleString('en-AU', { maximumFractionDigits: 1 })} %`;
}

/**
 * Explore view for enzyme activity: typed temperature and pH drive the worked steps and the
 * markers on both model curves from one state. The predict-first panel stays as Practice.
 */
export function PolymersEnzymeExplorer() {
  const [enzymeId, setEnzymeId] = useState(ENZYMES[0].id);
  const [input, setInput] = useState(DEFAULT_INPUT);
  const enzyme = ENZYMES.find((entry) => entry.id === enzymeId) ?? ENZYMES[0];
  const result = useMemo(() => calculateEnzymeExplore(enzyme, input), [enzyme, input]);

  return (
    <section className={CARD_CLASS} aria-labelledby="enzyme-explore-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Explore</p>
      <h3 id="enzyme-explore-heading" className="mt-1 text-xl font-extrabold text-ink">
        Type a temperature and pH
      </h3>
      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Choose an enzyme to explore">
              {ENZYMES.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  aria-pressed={entry.id === enzyme.id}
                  onClick={() => setEnzymeId(entry.id)}
                  className={chipClass(entry.id === enzyme.id)}
                >
                  {entry.name}
                </button>
              ))}
            </div>
            <p className="mt-2 text-sm font-medium leading-6 text-slate2">
              Model optimum for {enzyme.name}: {enzyme.optimumTemperatureC} °C, pH {enzyme.optimumPH}.
            </p>
          </div>
          <ModelInputPanel
            legend="Conditions"
            fields={ENZYME_EXPLORE_FIELDS}
            values={input}
            onChange={setInput}
            sliderKey="temperatureC"
          />
          <ExploreChallenges question={ENZYME_QUESTION} challenges={ENZYME_CHALLENGES} result={{ enzyme, result }} />
          <WorkedStepsPanel
            title="Activity working"
            steps={result.steps}
            stepTestIdPrefix="enzyme-step"
            resultTestId="enzyme-result"
            result={`Temperature curve: ${formatPct(result.temperatureActivity)} · pH curve: ${formatPct(result.phActivity)}`}
            limitNote={result.limitNote}
          />
        </div>
        <div className="min-w-0 space-y-4 rounded-[1.5rem] border border-brand-sky/20 bg-wash-sky p-4">
          <ModelLineChart
            series={[{ id: 'temperature', points: activityCurve(enzyme, 'temperature'), tone: 'coral' }]}
            xMin={TEMPERATURE_RANGE.min}
            xMax={TEMPERATURE_RANGE.max}
            yMax={PEAK_ACTIVITY}
            xLabel="Temperature (°C)"
            yLabel="Activity (%)"
            ariaLabel={`Explore: model activity of ${enzyme.name} against temperature, marker at ${input.temperatureC} °C, ${formatPct(result.temperatureActivity)}.`}
            marker={{ x: input.temperatureC, y: result.temperatureActivity }}
          />
          <ModelLineChart
            series={[{ id: 'ph', points: activityCurve(enzyme, 'ph'), tone: 'sky' }]}
            xMin={PH_RANGE.min}
            xMax={PH_RANGE.max}
            yMax={PEAK_ACTIVITY}
            xLabel="pH"
            yLabel="Activity (%)"
            ariaLabel={`Explore: model activity of ${enzyme.name} against pH, marker at pH ${input.pH}, ${formatPct(result.phActivity)}.`}
            marker={{ x: input.pH, y: result.phActivity }}
          />
          <p className="text-xs font-bold leading-5 text-slate2">
            Teaching model in relative units: each curve holds the other variable at its optimum.
          </p>
        </div>
      </div>
    </section>
  );
}
